// Global keyboard shortcuts for power-user trading operations.
//
// Usage:
//   useKeyboardShortcuts({
//     "k+control":  () => kill(),
//     "r+control":  () => refresh(),
//     "p+control":  () => pauseAll(),
//   })
//
// Shortcut format: "key+mod1+mod2" where mod = "control" | "shift" | "alt".
// Returns whether each shortcut is pending (cooldown) so the caller can
// show busy state.

import { useCallback, useEffect, useRef } from "react";

export type ShortcutDef = Record<
  string,
  { label: string; description: string; action: () => void }
>;

interface UseKeyboardShortcutsOpts {
  shortcuts: ShortcutDef;
}

/**
 * Register global keyboard shortcuts. Returns nothing — the callbacks
 * fire inline. Use cooldown inside your action if you need debounce.
 */
export function useKeyboardShortcuts({ shortcuts }: UseKeyboardShortcutsOpts) {
  const refMap = useRef<ShortcutDef>(shortcuts);
  refMap.current = shortcuts;

  const handler = useCallback((e: KeyboardEvent) => {
    // Skip when user is typing in an input.
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

    for (const [keyCombo, def] of Object.entries(refMap.current)) {
      const parts = keyCombo.split("+");
      const key = parts[0].toLowerCase();
      const mods = parts.slice(1);

      if (e.key.toLowerCase() !== key) continue;
      if (mods.includes("control") && !e.ctrlKey && !e.metaKey) continue;
      if (mods.includes("shift") && !e.shiftKey) continue;
      if (mods.includes("alt") && !e.altKey) continue;
      // If a modifier is *not* listed, ensure it's not pressed
      // (prevents Ctrl+Shift+K from also firing Ctrl+K).
      if (!mods.includes("control") && (e.ctrlKey || e.metaKey)) continue;
      if (!mods.includes("shift") && e.shiftKey) continue;
      if (!mods.includes("alt") && e.altKey) continue;

      e.preventDefault();
      e.stopPropagation();
      def.action();
      return;
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [handler]);
}

/**
 * Generate a human-readable shortcut label from a key combo string.
 * "k+control" → "Ctrl+K"
 */
export function shortcutLabel(keyCombo: string): string {
  const parts = keyCombo.split("+");
  const mods = parts.slice(1);
  const key = parts[0].toUpperCase();
  const modLabels: Record<string, string> = {
    control: "Ctrl",
    shift: "Shift",
    alt: "Alt",
  };
  return [...mods.map((m) => modLabels[m] ?? m), key].join("+");
}